import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer } from "@phosphor-icons/react";
import { InlineQR } from "@/components/QRCodeButton";
import UpiPayQR from "@/components/UpiPayQR";

export default function PublicQuotation() {
  const { id } = useParams();
  const params = new URLSearchParams(window.location.search);
  const token = params.get("t");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const autoprint = params.get("autoprint") === "1";

  useEffect(() => {
    fetch(`${API_BASE}/public/quotations/${id}?token=${encodeURIComponent(token || "")}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setData)
      .catch(() => setError("This quotation link is invalid or has expired."));
  }, [id, token]);

  useEffect(() => {
    if (data && autoprint) {
      const t = setTimeout(() => window.print(), 350);
      return () => clearTimeout(t);
    }
  }, [data, autoprint]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm">Loading…</div>;

  const q = data.quotation;
  const company = data.company || {};
  const intra = (q.cgst_total || 0) + (q.sgst_total || 0) > 0;
  const shareUrl = `${window.location.origin}/p/quotation/${q.id}?t=${q.share_token}`;

  return (
    <div className="min-h-screen bg-white text-black" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="max-w-4xl mx-auto p-10 print:p-6">
        <div className="flex justify-end mb-6 print:hidden">
          <Button onClick={() => window.print()} className="h-9"><Printer size={14} className="mr-1.5" /> Print / Save PDF</Button>
        </div>

        <div className="border-2 border-black">
          <div className="p-6 border-b-2 border-black flex items-center justify-between gap-6">
            <img
              src={company.logo_url || "/brand/vetrich-logo.png"}
              alt={company.name || "Logo"}
              style={{ height: "96px", width: "auto", objectFit: "contain", flexShrink: 0 }}
              crossOrigin="anonymous"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>Quotation</div>
              <h1 className="text-2xl font-bold tracking-tight mt-1" style={{ fontFamily: "'Chivo', sans-serif" }}>{company.name || "Vetrich"}</h1>
              <div className="text-[12px] text-gray-600 mt-1.5 whitespace-pre-line">{company.address}</div>
              <div className="text-[12px] mt-1">GSTIN: <span className="font-mono">{company.gstin}</span> · State: {company.state} ({company.state_code})</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>Quotation #</div>
              <div className="text-lg font-mono font-bold mt-1">{q.number}</div>
              <div className="mt-2 text-[12px]">Date: <span className="font-mono">{q.date}</span></div>
              {q.valid_till && <div className="text-[12px]">Valid Till: <span className="font-mono">{q.valid_till}</span></div>}
              <div className="mt-3 flex justify-end gap-3 items-start">
                <InlineQR url={shareUrl} size={68} caption="Scan" />
              </div>
            </div>
          </div>

          <div className="p-6 grid grid-cols-2 gap-8 border-b-2 border-black">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5" style={{ fontFamily: "'Chivo', sans-serif" }}>Quotation For</div>
              <div className="font-bold text-base">{q.customer_name}</div>
              {q.customer_state_code && <div className="text-[12px] mt-1">State Code: <span className="font-mono">{q.customer_state_code}</span></div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5" style={{ fontFamily: "'Chivo', sans-serif" }}>Status</div>
              <div className="font-semibold uppercase">{q.status}</div>
            </div>
          </div>

          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b-2 border-black" style={{ fontFamily: "'Chivo', sans-serif" }}>
                <th className="text-left p-3 uppercase text-[10px] tracking-widest">#</th>
                <th className="text-left p-3 uppercase text-[10px] tracking-widest">Description</th>
                <th className="text-left p-3 uppercase text-[10px] tracking-widest">HSN</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Qty</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Rate</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Tax</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Total</th>
              </tr>
            </thead>
            <tbody>
              {q.items.map((it, i) => (
                <tr key={it.id || it.product_id || `${it.hsn}-${it.name}-${i}`} className="border-b border-gray-300">
                  <td className="p-3 font-mono">{i + 1}</td>
                  <td className="p-3">{it.name}</td>
                  <td className="p-3 font-mono">{it.hsn}</td>
                  <td className="p-3 text-right font-mono">{it.quantity} {it.unit}</td>
                  <td className="p-3 text-right font-mono">{inr(it.rate)}</td>
                  <td className="p-3 text-right font-mono">{it.tax_rate}%</td>
                  <td className="p-3 text-right font-mono font-semibold">{inr(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border-t-2 border-black flex">
            <div className="flex-1 p-6 text-[12px]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Notes / Work Name</div>
              <div className="whitespace-pre-line">{q.notes || "—"}</div>
            </div>
            <div className="w-72 border-l-2 border-black p-6 text-[13px] space-y-1.5">
              <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-mono">{inr(q.subtotal)}</span></div>
              {intra ? (
                <>
                  <div className="flex justify-between"><span className="text-gray-600">CGST</span><span className="font-mono">{inr(q.cgst_total)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">SGST</span><span className="font-mono">{inr(q.sgst_total)}</span></div>
                </>
              ) : (
                <div className="flex justify-between"><span className="text-gray-600">IGST</span><span className="font-mono">{inr(q.igst_total)}</span></div>
              )}
              <div className="border-t-2 border-black pt-2 mt-2 flex justify-between font-bold text-base" style={{ fontFamily: "'Chivo', sans-serif" }}>
                <span>Grand Total</span><span className="font-mono">{inr(q.grand_total)}</span>
              </div>
            </div>
          </div>

          <div className="p-6 border-t-2 border-black flex items-end justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              {(data.company || {}).upi_id && Number(q.grand_total) > 0 && q.status !== "converted" && (
                <UpiPayQR
                  vpa={data.company.upi_id}
                  payeeName={(data.company || {}).name}
                  amount={q.grand_total}
                  reference={q.number}
                  size={82}
                  caption="Advance"
                />
              )}
              {q.status === "converted" && (
                <div className="border-2 border-emerald-600 rounded-md bg-emerald-50 p-2 text-center min-w-[82px]">
                  <div className="text-[9px] uppercase tracking-[0.16em] text-emerald-700 font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>
                    Converted
                  </div>
                  <div className="text-[9px] text-emerald-800 mt-0.5">QR disabled</div>
                </div>
              )}
              <div className="text-[11px] text-gray-600 self-end">This quotation is valid until the date mentioned above.</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>For {company.name}</div>
              <div className="mt-8 text-[12px] font-semibold">Authorised Signatory</div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>
          Powered by {company.name || "Vetrich Technologies"} · Smart Accounting. Smarter Business.
        </div>
      </div>
    </div>
  );
}
